// Shared blog content helpers — used by the public blog pages and the admin
// editor. A post's `content` column can hold three shapes, all produced by code
// that already shipped:
//   1. A JSON array of editor blocks  (the block editor: JSON.stringify(blocks))
//   2. A JSON-stringified HTML string (POST /api/blog wraps body.content in JSON)
//   3. A raw HTML string              (legacy / direct DB rows)
// renderBlogContent() normalises all three to safe HTML for dangerouslySetInnerHTML.

import sanitizeHtml from 'sanitize-html'

export interface BlogBlock {
  id?: string
  type?: string
  content?: string
  meta?: Record<string, string>
}

/**
 * Sanitizer allowlist (audit 2026-07). Blog `content` can be stored as raw HTML
 * or JSON-stringified HTML (legacy/direct rows), and those paths were previously
 * fed straight into dangerouslySetInnerHTML. Authoring is admin-only, but a
 * compromised or lower-tier admin account would have meant full-site script
 * execution. Every render path now passes through this allowlist, which permits
 * only the formatting tags the block editor emits — no <script>, no event
 * handlers, no javascript: URLs.
 */
const BLOG_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    'h2', 'h3', 'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's',
    'blockquote', 'cite', 'div', 'span', 'pre', 'code', 'hr',
    'ul', 'ol', 'li', 'figure', 'figcaption', 'img', 'a',
  ],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
    img: ['src', 'alt'],
    div: ['class'],
    span: ['class'],
    p: ['class'],
    blockquote: ['class', 'dir'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  allowedSchemesByTag: { img: ['http', 'https', 'data'] },
  // Force safe rel on any anchor that opens a new tab.
  transformTags: {
    a: sanitizeHtml.simpleTransform('a', { rel: 'noopener noreferrer' }),
  },
  disallowedTagsMode: 'discard',
}

/** Final safety gate applied to every HTML string before it reaches the DOM. */
export function sanitizeBlogHtml(html: string): string {
  return sanitizeHtml(html, BLOG_SANITIZE_OPTIONS)
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, '&#39;')
}

function blocksToHtml(blocks: BlogBlock[]): string {
  return blocks
    .map((b) => {
      const raw = b?.content || ''
      const c = escapeHtml(raw)
      switch (b?.type) {
        case 'h2': return `<h2>${c}</h2>`
        case 'h3': return `<h3>${c}</h3>`
        case 'quote': return `<blockquote>${c}</blockquote>`
        case 'ayah': return `<blockquote class="blog-ayah" dir="rtl">${c}${b.meta?.attribution ? `<cite>— ${escapeHtml(b.meta.attribution)}</cite>` : ''}</blockquote>`
        case 'callout': return `<div class="blog-callout">💡 ${c}</div>`
        case 'code': return `<pre><code>${c}</code></pre>`
        case 'divider': return '<hr/>'
        case 'image':
          return raw ? `<figure><img src="${escapeAttr(raw)}" alt="${escapeAttr(b.meta?.alt || b.meta?.caption || '')}"/>${b.meta?.caption ? `<figcaption>${escapeHtml(b.meta.caption)}</figcaption>` : ''}</figure>` : ''
        case 'embed':
          return raw ? `<p class="blog-embed"><a href="${escapeAttr(raw)}" target="_blank" rel="noopener noreferrer">${c || escapeHtml(raw)}</a></p>` : ''
        case 'bullet':
          return `<ul>${raw.split('\n').filter(Boolean).map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul>`
        case 'numbered':
          return `<ol>${raw.split('\n').filter(Boolean).map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ol>`
        default:
          return c ? `<p>${c.replace(/\n/g, '<br/>')}</p>` : ''
      }
    })
    .filter(Boolean)
    .join('\n')
}

/** Normalise any stored `content` value to renderable HTML. */
export function renderBlogContent(raw: unknown): string {
  if (!raw || typeof raw !== 'string') return ''
  let html: string
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    html = raw // already raw HTML
    return sanitizeBlogHtml(html)
  }
  if (typeof parsed === 'string') html = parsed // JSON-stringified HTML
  else if (Array.isArray(parsed)) html = blocksToHtml(parsed as BlogBlock[])
  else html = raw
  // Sanitize EVERY path — even editor-block output, as defence in depth.
  return sanitizeBlogHtml(html)
}

/** Plain-text length → words → minutes (200 wpm), min 1. */
export function estimateReadTime(raw: unknown): number {
  const html = renderBlogContent(raw)
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  const words = text ? text.split(' ').length : 0
  return Math.max(1, Math.ceil(words / 200))
}

/** Best-effort plain-text excerpt when a post has no stored excerpt. */
export function excerptFrom(raw: unknown, max = 160): string {
  const text = renderBlogContent(raw).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  if (text.length <= max) return text
  return text.slice(0, max).replace(/\s+\S*$/, '') + '…'
}
